/**
 * User Settings Page
 * Comprehensive settings for profile, security (2FA, passkeys), and account management
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProfileSection from '../components/settings/ProfileSection';
import SecuritySection from '../components/settings/SecuritySection';
import PasswordChange from '../components/settings/PasswordChange';
import AccountSection from '../components/settings/AccountSection';
import { USER_SETTINGS_TABS } from '../config/constants';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 80vh;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: var(--accent-primary);
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: var(--text-primary);
  opacity: 0.7;
  font-size: 1.1rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  border-bottom: 2px solid var(--border-color);
  margin-bottom: 2rem;
  overflow-x: auto;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--accent-primary)' : 'var(--text-primary)'};
  cursor: pointer;
  border-bottom: 3px solid ${props => props.$active ? 'var(--accent-primary)' : 'transparent'};
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: var(--accent-primary);
    background: var(--bg-secondary);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
`;

const TabIcon = styled.span`
  margin-right: 0.5rem;
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

function UserSettings() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!user) {
    return null;
  }

  return (
    <Container>
      <Header>
        <Title>Account Settings</Title>
        <Subtitle>Manage your profile, security, and account preferences</Subtitle>
      </Header>

      <TabContainer>
        {USER_SETTINGS_TABS.map(tab => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <TabIcon>{tab.icon}</TabIcon>
            {tab.label}
          </Tab>
        ))}
      </TabContainer>

      <TabContent>
        {activeTab === 'profile' && <ProfileSection user={user} />}
        {activeTab === 'security' && <SecuritySection user={user} />}
        {activeTab === 'password' && <PasswordChange />}
        {activeTab === 'account' && <AccountSection />}
      </TabContent>
    </Container>
  );
}

export default UserSettings;
